import { draftStore } from '../../../lib/draft';
import { fmtDuration, ladderRows, RUNNER_MPS, type LadderRow } from '../../../lib/s3/ladder';
import type { VehicleDef } from '../../../lib/s3/types';
import type { Bundle } from '../../../lib/validate';
import { NumberInput } from '../fields';
import { useContentBundle } from '../refs';

/** The ladder's editable numbers: [header, path into the vehicle, integer]. */
const EDITABLE: [string, string[], boolean][] = [
  ['Top m/s', ['topSpeedMps'], false],
  ['Road km/h', ['roadKmh'], false],
  ['HP', ['maxHealth'], true],
  ['Cargo kg', ['cargoKg'], false],
  ['Idle m', ['noise', 'idleM'], false],
  ['Full m', ['noise', 'fullM'], false],
  ['Horn m', ['noise', 'hornM'], false],
  ['Tank s', ['fuel', 'tankS'], false],
  ['Unlock', ['unlockAfterLureRuns'], true],
];

const read = (v: VehicleDef, path: string[]) => {
  let x: unknown = v;
  for (const k of path) x = (x as Record<string, unknown> | null | undefined)?.[k];
  return typeof x === 'number' ? x : undefined;
};

const write = (v: VehicleDef, path: string[], n: number): VehicleDef => {
  if (path.length === 1) return { ...v, [path[0]!]: n };
  const [head, key] = path as [keyof VehicleDef, string];
  return { ...v, [head]: { ...((v[head] as object | null | undefined) ?? {}), [key]: n } };
};

/** The page's starting values from the draft: the runner's speed, the grab numbers, lent items' noise. */
function useLadder(km = 1): { vehicles: VehicleDef[]; rows: LadderRow[]; runnerMps: number } {
  const b = useContentBundle();
  const vehicles = ((b.vehicles ?? []) as (VehicleDef | null)[]).filter(
    (x): x is VehicleDef => x != null,
  );
  const runnerMps = (b.zombies ?? []).find((z) => z?.id === 'runner')?.moveSpeedMps ?? RUNNER_MPS;
  const rows = ladderRows(vehicles, {
    km,
    runnerMps,
    grab: b.arenaRules?.grab ?? undefined,
    itemNoiseM: (id) => (b.items ?? []).find((i) => i?.id === id)?.noiseRadiusM ?? 0,
  });
  return { vehicles, rows, runnerMps };
}

/**
 * The vehicle ladder (lurerun.html "The vehicle ladder"): every rung's main numbers as an editable table (edits go straight into the
 * draft), then the side-by-side comparison: speed against a runner, noise radii drawn as rings at one scale, and the drive for 1 km.
 */
export function VehicleLadder() {
  const { vehicles, rows, runnerMps } = useLadder();
  const all = (useContentBundle().vehicles ?? []) as (VehicleDef | null)[];
  if (!vehicles.length)
    return (
      <p className="mb-4 text-sm text-bruise">
        No vehicles in the draft yet. Add the five rungs (e-bike, motorcycle, 4x4, pickup, the
        technical) below.
      </p>
    );
  const setVehicle = (id: string, next: VehicleDef) =>
    draftStore.update(
      (b) =>
        ({
          ...b,
          vehicles: all.map((x) => (x?.id === id ? next : x)),
        }) as Bundle,
    );
  return (
    <section className="mb-6 space-y-4" aria-label="The vehicle ladder">
      <div className="overflow-x-auto">
        <table className="w-full border-2 border-ink bg-white text-sm">
          <caption className="bg-ink px-2 py-1 text-left font-bold text-newsprint">
            The ladder (edit in place)
          </caption>
          <thead className="bg-newsprint">
            <tr>
              <th className="px-2 py-1 text-left">Vehicle</th>
              <th className="px-2 py-1 text-left">Seats</th>
              {EDITABLE.map(([h]) => (
                <th key={h} className="px-2 py-1 text-left">
                  {h}
                </th>
              ))}
              <th className="px-2 py-1 text-left">Ram</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.map((v, i) => (
              <tr key={`${v.id}-${i}`} className="border-t border-newsprint">
                <td className="px-2 py-1">
                  <span className="font-mono">{v.id}</span>
                  {v.nickname ? <span className="text-bruise"> · {v.nickname}</span> : null}
                </td>
                <td className="px-2 py-1">
                  {rows[i]!.seats} ({rows[i]!.gunners} gun)
                </td>
                {EDITABLE.map(([h, path, integer]) => (
                  <td key={h} className="w-20 px-1 py-0.5">
                    <NumberInput
                      label={`${v.id} ${h}`}
                      integer={integer}
                      min={0}
                      value={read(v, path)}
                      onChange={(n) => setVehicle(v.id ?? '', write(v, path, n ?? 0))}
                    />
                  </td>
                ))}
                <td className="px-2 py-1">
                  {rows[i]!.canRam ? `≥ ${v.ram?.minSpeedMps} m/s` : 'no'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LadderComparison rows={rows} runnerMps={runnerMps} />
    </section>
  );
}

const RING_SIZE = 150;

/** Noise rings for one vehicle at a shared scale: idle (solid), full throttle (dashed), horn (faint), the lent gun (red). */
function Rings({ row, maxM }: { row: LadderRow; maxM: number }) {
  const c = RING_SIZE / 2;
  const r = (m: number) => (maxM > 0 ? (m / maxM) * (c - 4) : 0);
  const ring = (m: number, cls: string, dash?: string, label?: string) =>
    m > 0 ? (
      <circle
        cx={c}
        cy={c}
        r={r(m)}
        className={cls}
        strokeDasharray={dash}
        fill="none"
        strokeWidth={2}
      >
        <title>{label}</title>
      </circle>
    ) : null;
  return (
    <figure className="flex flex-col items-center">
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        role="img"
        aria-label={`${row.id} noise rings`}
      >
        {ring(row.noise.hornM, 'stroke-caption', undefined, `horn ${row.noise.hornM} m`)}
        {ring(row.noise.gunM, 'stroke-blam', '2 3', `lent gun ${row.noise.gunM} m`)}
        {ring(row.noise.fullM, 'stroke-ink', '5 3', `full throttle ${row.noise.fullM} m`)}
        {ring(row.noise.idleM, 'stroke-ink', undefined, `idle ${row.noise.idleM} m`)}
        <circle cx={c} cy={c} r={3} className="fill-benday" />
      </svg>
      <figcaption className="text-center text-xs">
        <span className="font-mono font-semibold">{row.id}</span>
        <br />
        {row.noise.idleM}/{row.noise.fullM}/{row.noise.hornM} m
        {row.noise.gunM > 0 ? ` · gun ${row.noise.gunM}` : ''}
      </figcaption>
    </figure>
  );
}

/** The comparison: rings, speed against a runner, the 1 km drive against the walk, and the grab time for a full load. */
export function LadderComparison({ rows, runnerMps }: { rows: LadderRow[]; runnerMps: number }) {
  const maxM = Math.max(1, ...rows.flatMap((r) => [r.noise.hornM, r.noise.gunM, r.noise.fullM]));
  const maxSpeed = Math.max(runnerMps, ...rows.map((r) => r.topSpeedMps)) * 1.1;
  return (
    <div className="rounded border-2 border-ink bg-white p-3">
      <h2 className="mb-2 text-sm font-bold">Side by side</h2>
      <p className="mb-2 text-xs text-bruise">
        Rings share one scale (largest {maxM} m): idle solid, full throttle dashed, horn yellow, the
        lent gun red. The drive is a team move: 1 km x 1.3 detour at the road speed.
      </p>
      <div className="flex flex-wrap gap-4">
        {rows.map((r) => (
          <Rings key={r.id} row={r} maxM={maxM} />
        ))}
      </div>
      <table className="mt-3 w-full text-sm">
        <thead>
          <tr className="text-left">
            <th className="px-2">Vehicle</th>
            <th className="px-2">Top speed vs runner ({runnerMps} m/s)</th>
            <th className="px-2">Drive 1 km</th>
            <th className="px-2">Energy</th>
            <th className="px-2">Full grab</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-newsprint">
              <td className="px-2 font-mono">{r.id}</td>
              <td className="px-2">
                <svg
                  width="100%"
                  height="14"
                  viewBox="0 0 200 14"
                  preserveAspectRatio="none"
                  aria-hidden
                >
                  <rect
                    x={0}
                    y={2}
                    height={10}
                    width={(r.topSpeedMps / maxSpeed) * 200}
                    className={r.runnerMarginMps > 0 ? 'fill-benday' : 'fill-blam'}
                  />
                  <line
                    x1={(runnerMps / maxSpeed) * 200}
                    x2={(runnerMps / maxSpeed) * 200}
                    y1={0}
                    y2={14}
                    className="stroke-ink"
                    strokeWidth={2}
                  />
                </svg>
                <span
                  className={`text-xs ${r.runnerMarginMps > 0 ? '' : 'font-semibold text-blam'}`}
                >
                  {r.topSpeedMps} m/s ({r.runnerMarginMps >= 0 ? '+' : ''}
                  {Math.round(r.runnerMarginMps * 10) / 10})
                </span>
              </td>
              <td className="px-2">
                {fmtDuration(r.driveS)}{' '}
                <span className="text-xs text-bruise">(walk {fmtDuration(r.walkS)})</span>
              </td>
              <td className="px-2">{r.tripEnergy}</td>
              <td className="px-2">{Math.round(r.grabS * 10) / 10} s</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
